import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import Layout from "components/Layout";

// Page Imports
import CompanyUserLogin from "pages/company-user-login";
import DashboardOverview from "pages/dashboard-overview";
import MisReport from "pages/mis-report";
import VendorApprovalWorkflow from "pages/vendor-approval-workflow";
import PublicVendorRegistrationForm from "pages/public-vendor-registration-form";
import PublicVendorAcknowledgement from "pages/public-vendor-acknowledgement";
import ConflictingVendors from "pages/conflicting-vendors";
import VendorCodeConflicts from "pages/vendor-code-conflicts";
import VendorMasterList from "pages/vendor-master-list";
import VendorProfileDetails from "pages/vendor-profile-details";
import VendorExcelImport from "pages/vendor-excel-import";
import SupplierRequestForm from "pages/supplier-request-form";
import SupplierRequests from "pages/supplier-requests";
import UserManagement from "pages/user-management";
import InviteManagement from "pages/invite-management";
import {
  CampaignShell,
  CampaignDashboardPage,
  CampaignCampaignsPage,
  CampaignTemplatesPage,
  CampaignTemplateEditorPage,
  CampaignAudiencePage,
  CampaignAnalyticsPage,
  CampaignRegularizationPage,
  CampaignThroughVendorWizard,
  CampaignSelfRegularizationHub,
  CampaignSelfRegularizationWizard,
  CampaignSelfRegularizationRun,
  CampaignSurepassUsagePage,
  CampaignBroadcastPage,
} from "pages/campaigning-tool/shell";
import BatchDetails from "pages/vendor-regularization/batch-details/[batchId]";
import VendorRegularizationForm from "pages/vendor-regularization-form";
import VendorVerification from "pages/vendor-verification";
import PublicDocumentRequest from "pages/public-document-request";
import VendorDocumentsPage from "pages/vendor-documents";
import ApprovalDecisionPage from "pages/approval-decision";
import PlatformConsumers from "pages/platform-admin/consumers";
import PlatformDefinitions from "pages/platform-admin/api-definitions";
import PlatformUsage from "pages/platform-admin/usage";
import PlatformAuditLogs from "pages/platform-admin/audit-logs";
import NotFound from "pages/NotFound";

// ProtectedRoute component
function ProtectedRoute({ children, requireAdmin = false, requireAdminOrCoordinator = false, requireSuperAdmin = false }) {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/company-user-login" replace />;
  }

  // Platform (API Management) accounts are a distinct, console-only role: they must not see or
  // reach any client-facing section. Block every non-console route and bounce to the console.
  // Keyed off isPlatformSuperAdmin (NOT the base role). Must run before the base-role checks.
  if (user?.isPlatformSuperAdmin && !requireSuperAdmin) {
    return <Navigate to="/platform-admin/consumers" replace />;
  }

  // Super-admin (API Management) gate — client-side UX only; the backend 404 is the real boundary.
  if (requireSuperAdmin && !user?.isPlatformSuperAdmin) {
    return <Navigate to="/supplier-requests" replace />;
  }

  // Check if admin-only access is required (case-insensitive)
  if (requireAdmin && user?.role?.toUpperCase() !== 'ADMIN') {
    return <Navigate to="/supplier-requests" replace />;
  }
  
  // Check if admin or coordinator access is required (case-insensitive)
  if (requireAdminOrCoordinator) {
    const role = user?.role?.toUpperCase();
    if (role !== 'ADMIN' && role !== 'COORDINATOR') {
      return <Navigate to="/supplier-requests" replace />;
    }
  }
  
  return children;
}

// RoleBasedRedirect component
function RoleBasedRedirect() {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/company-user-login" replace />;
  }

  // Platform (API Management) accounts are console-only: home is the API Management console,
  // never a client-facing landing page. Checked first, keyed off isPlatformSuperAdmin.
  if (user?.isPlatformSuperAdmin) {
    return <Navigate to="/platform-admin/consumers" replace />;
  }

  const role = user?.role?.toUpperCase();
  if (role === 'ADMIN' || role === 'COORDINATOR') {
    return <Navigate to="/dashboard-overview" replace />;
  } else {
    return <Navigate to="/supplier-requests" replace />;
  }
}

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          {/* Public Routes */}
          <Route path="/" element={<RoleBasedRedirect />} />
          <Route path="/company-user-login" element={<CompanyUserLogin />} />
          <Route path="/public-vendor-registration-form" element={<PublicVendorRegistrationForm />} />
          <Route path="/public-vendor-acknowledgement" element={<PublicVendorAcknowledgement />} />
          <Route path="/vendor-regularization-form" element={<VendorRegularizationForm />} />
          {/* Self-regularization: the vendor confirms what an approved run wrote to their record. */}
          <Route path="/vendor-verification" element={<VendorVerification />} />
          <Route path="/public-document-request" element={<PublicDocumentRequest />} />
          <Route path="/vendor-documents" element={<VendorDocumentsPage />} />
          <Route path="/approval-decision" element={<ApprovalDecisionPage />} />

          {/* Protected Routes (Wrapped in Layout) */}
          <Route element={<Layout />}>
            {/* Dashboard - Admin & Coordinator */}
            <Route path="/dashboard-overview" element={
              <ProtectedRoute requireAdminOrCoordinator={true}>
                <DashboardOverview />
              </ProtectedRoute>
            } />

            {/* MIS Report - Admin & Coordinator */}
            <Route path="/mis-report" element={
              <ProtectedRoute requireAdminOrCoordinator={true}>
                <MisReport />
              </ProtectedRoute>
            } />
            
            {/* Vendor Approval - Admin & Coordinator */}
            <Route path="/vendor-approval-workflow" element={
              <ProtectedRoute requireAdminOrCoordinator={true}>
                <VendorApprovalWorkflow />
              </ProtectedRoute>
            } />
            
            {/* Vendor Master List - Admin & Coordinator */}
            <Route path="/vendor-master-list" element={
              <ProtectedRoute requireAdminOrCoordinator={true}>
                <VendorMasterList />
              </ProtectedRoute>
            } />
            
            {/* Vendor Profile - Admin & Coordinator */}
            <Route path="/vendor-profile-details/:vendorId" element={
              <ProtectedRoute requireAdminOrCoordinator={true}>
                <VendorProfileDetails />
              </ProtectedRoute>
            } />
            
            {/* Excel Import - Admin Only */}
            <Route path="/vendor-excel-import" element={
              <ProtectedRoute requireAdmin={true}>
                <VendorExcelImport />
              </ProtectedRoute>
            } />
            
            {/* User Management - Admin Only */}
            <Route path="/user-management" element={
              <ProtectedRoute requireAdmin={true}>
                <UserManagement />
              </ProtectedRoute>
            } />
            
            {/* Invite Management - Admin & Coordinator */}
            <Route path="/invite-management" element={
              <ProtectedRoute requireAdminOrCoordinator={true}>
                <InviteManagement />
              </ProtectedRoute>
            } />
            
            {/* Conflicting Vendors - Admin & Coordinator */}
            <Route path="/conflicting-vendors" element={
              <ProtectedRoute requireAdminOrCoordinator={true}>
                <ConflictingVendors />
              </ProtectedRoute>
            } />

            {/* Vendor Code Conflicts (ERP sync) - Admin & Coordinator */}
            <Route path="/vendor-code-conflicts" element={
              <ProtectedRoute requireAdminOrCoordinator={true}>
                <VendorCodeConflicts />
              </ProtectedRoute>
            } />
            
            {/* Back-compat: the standalone Regularization page was retired — its reduced, live block
                now lives inside the CampaignOS Dashboard. Old links redirect there. */}
            <Route path="/vendor-regularization" element={<Navigate to="/campaigning-tool/regularization" replace />} />
            <Route path="/vendor-regularization/batch/:batchId" element={
              <ProtectedRoute requireAdmin={true}>
                <BatchDetails />
              </ProtectedRoute>
            } />
            
            {/* Supplier Request Form - All Authenticated Users */}
            <Route path="/supplier-request-form" element={
              <ProtectedRoute>
                <SupplierRequestForm />
              </ProtectedRoute>
            } />
            
            {/* Supplier Requests List - All Authenticated Users */}
            <Route path="/supplier-requests" element={
              <ProtectedRoute>
                <SupplierRequests />
              </ProtectedRoute>
            } />

            {/* Platform Admin / API Management - super-admin only */}
            <Route path="/platform-admin/consumers" element={
              <ProtectedRoute requireSuperAdmin={true}>
                <PlatformConsumers />
              </ProtectedRoute>
            } />
            <Route path="/platform-admin/api-definitions" element={
              <ProtectedRoute requireSuperAdmin={true}>
                <PlatformDefinitions />
              </ProtectedRoute>
            } />
            <Route path="/platform-admin/usage" element={
              <ProtectedRoute requireSuperAdmin={true}>
                <PlatformUsage />
              </ProtectedRoute>
            } />
            <Route path="/platform-admin/audit-logs" element={
              <ProtectedRoute requireSuperAdmin={true}>
                <PlatformAuditLogs />
              </ProtectedRoute>
            } />
          </Route>

          {/* Campaigning Tool — CampaignOS full-takeover shell. Deliberately OUTSIDE the VMS <Layout>
              group above so it replaces VMS's sidebar/header entirely with the CampaignOS mini
              sidebar. Admin-only: every screen below reads the live API. Self-regularization gates
              its own write actions on isAdmin so this route can later open to coordinators. */}
          <Route element={
            <ProtectedRoute requireAdmin={true}>
              <CampaignShell />
            </ProtectedRoute>
          }>
            <Route path="/campaigning-tool" element={<CampaignDashboardPage />} />
            <Route path="/campaigning-tool/campaigns" element={<CampaignCampaignsPage />} />
            <Route path="/campaigning-tool/templates" element={<CampaignTemplatesPage />} />
            {/* Full-page template editor: scratch pad (or guided form) beside the live preview. */}
            <Route path="/campaigning-tool/templates/new" element={<CampaignTemplateEditorPage />} />
            <Route path="/campaigning-tool/templates/:id" element={<CampaignTemplateEditorPage />} />
            <Route path="/campaigning-tool/audience" element={<CampaignAudiencePage />} />
            <Route path="/campaigning-tool/analytics" element={<CampaignAnalyticsPage />} />
            <Route path="/campaigning-tool/broadcast" element={<CampaignBroadcastPage />} />
            <Route path="/campaigning-tool/surepass-usage" element={<CampaignSurepassUsagePage />} />
            <Route path="/campaigning-tool/regularization" element={<CampaignRegularizationPage />} />
            <Route path="/campaigning-tool/regularization/send" element={<CampaignThroughVendorWizard />} />
            {/* Self-regularization: verify what is on record against the registries, no email to the vendor. */}
            <Route path="/campaigning-tool/regularization/self/new" element={<CampaignSelfRegularizationWizard />} />
          </Route>

          {/* Self-regularization is read-only for coordinators: they may see runs and results, never start
              or approve one (money moves). The shell shows them nothing else. */}
          <Route element={
            <ProtectedRoute requireAdminOrCoordinator={true}>
              <CampaignShell />
            </ProtectedRoute>
          }>
            <Route path="/campaigning-tool/regularization/self" element={<CampaignSelfRegularizationHub />} />
            <Route path="/campaigning-tool/regularization/self/runs/:runId" element={<CampaignSelfRegularizationRun />} />
          </Route>

          {/* 404 Route */}
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
